import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, TrendingUp, Coffee, Star, Search, Filter, Trophy,
  Car, Bus, Building2, UserCheck, UserX, MoreVertical, 
  RefreshCw, Bell, ChevronUp, ChevronDown, Award, DollarSign
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// Partner type icons
const PARTNER_TYPE_ICONS = {
  taxi: Car,
  tour_bus: Bus,
  hotel: Building2,
  other: Users
};

const PARTNER_TYPE_LABELS = {
  taxi: 'Taxi Driver',
  tour_bus: 'Tour Bus',
  hotel: 'Hotel/Resort',
  other: 'Other'
};

export default function PartnerManagementTab() {
  const [stats, setStats] = useState(null);
  const [partners, setPartners] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('total_accumulated');
  const [selectedPartner, setSelectedPartner] = useState(null);

  // Fetch all data
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, filterType]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, partnersRes, leaderboardRes] = await Promise.all([
        api.get('/partners/admin/stats'),
        api.get('/partners/admin/list', { params: { sort_by: sortBy, ...(filterType !== 'all' && { partner_type: filterType }) } }),
        api.get('/partners/admin/leaderboard', { params: { limit: 10 } })
      ]);

      setStats(statsRes.data);
      setPartners(partnersRes.data.partners || []);
      setLeaderboard(leaderboardRes.data.leaderboard || []);
    } catch (err) {
      console.error('Failed to fetch partner data:', err);
      toast.error('Failed to load partner data');
    }
    setLoading(false);
  };

  // Update partner status
  const updatePartnerStatus = async (partnerId, newStatus) => {
    try {
      await api.put(`/partners/admin/partner/${partnerId}/status`, null, { params: { status: newStatus } });
      toast.success(`Partner status updated to ${newStatus}`);
      fetchData();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  // Nudge inactive partners
  const nudgeInactive = async () => {
    try {
      const res = await api.post('/partners/admin/nudge-inactive', null, { params: { days_inactive: 14 } });
      toast.success(`Nudged ${res.data.nudged} inactive partners`);
    } catch (err) {
      toast.error('Failed to send nudges');
    }
  };

  // Filter partners by search
  const filteredPartners = partners.filter(p => 
    p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.phone?.includes(searchQuery)
  );

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="partner-management-tab">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Partner Program</h2>
          <p className="text-muted-foreground">Manage taxi drivers, tour guides, and hotel partners</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={nudgeInactive} data-testid="nudge-inactive-btn">
            <Bell className="w-4 h-4 mr-2" />
            Nudge Inactive
          </Button>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card p-4 rounded-xl border border-border"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats.partners?.total || 0}</p>
                <p className="text-xs text-muted-foreground">Total Partners</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-card p-4 rounded-xl border border-border"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Star className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats.partners?.vip || 0}</p>
                <p className="text-xs text-muted-foreground">VIP Partners</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-card p-4 rounded-xl border border-border"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">${stats.revenue?.total?.toFixed(0) || 0}</p>
                <p className="text-xs text-muted-foreground">Total Revenue</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-card p-4 rounded-xl border border-border"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats.referrals?.total || 0}</p>
                <p className="text-xs text-muted-foreground">Total Referrals</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-card p-4 rounded-xl border border-border"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                <Coffee className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats.coffees?.earned || 0}</p>
                <p className="text-xs text-muted-foreground">Coffees Earned</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-card p-4 rounded-xl border border-border"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                <Coffee className="w-5 h-5 text-teal-600 dark:text-teal-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats.coffees?.outstanding || 0}</p>
                <p className="text-xs text-muted-foreground">Coffees Pending</p>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Leaderboard & Partner List */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Leaderboard */}
        <div className="lg:col-span-1">
          <div className="bg-card rounded-xl border border-border overflow-hidden">
            <div className="p-4 border-b border-border bg-gradient-to-r from-amber-500/10 to-yellow-500/10">
              <div className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-500" />
                <h3 className="font-semibold text-foreground">Top Partners</h3>
              </div>
            </div>
            <div className="divide-y divide-border">
              {leaderboard.slice(0, 10).map((partner, index) => {
                const TypeIcon = PARTNER_TYPE_ICONS[partner.partner_type] || Users;
                return (
                  <div 
                    key={partner.id} 
                    className="p-3 flex items-center gap-3 hover:bg-muted/50 transition-colors"
                    data-testid={`leaderboard-item-${index}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                      index === 0 ? 'bg-amber-500 text-white' :
                      index === 1 ? 'bg-gray-400 text-white' :
                      index === 2 ? 'bg-orange-600 text-white' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-foreground truncate">{partner.name}</p>
                        {partner.is_vip && <Star className="w-3 h-3 text-amber-500 fill-amber-500" />}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <TypeIcon className="w-3 h-3" />
                        <span>{partner.code}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-foreground">{partner.coffees_earned}</p>
                      <p className="text-xs text-muted-foreground">coffees</p>
                    </div>
                  </div>
                );
              })}
              {leaderboard.length === 0 && (
                <div className="p-8 text-center text-muted-foreground">
                  <Trophy className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No partners yet</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Partner List */}
        <div className="lg:col-span-2">
          <div className="bg-card rounded-xl border border-border">
            <div className="p-4 border-b border-border">
              <div className="flex flex-col md:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, code, or phone..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="partner-search-input"
                  />
                </div>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-border bg-background text-foreground"
                  data-testid="partner-type-filter"
                >
                  <option value="all">All Types</option>
                  <option value="taxi">Taxi Drivers</option>
                  <option value="tour_bus">Tour Bus</option>
                  <option value="hotel">Hotel/Resort</option>
                  <option value="other">Other</option>
                </select>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-border bg-background text-foreground"
                  data-testid="partner-sort-select"
                >
                  <option value="total_accumulated">By Revenue</option>
                  <option value="total_referrals">By Referrals</option>
                  <option value="coffees_earned">By Coffees</option>
                  <option value="created_at">By Join Date</option>
                </select>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground">Partner</th>
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground">Code</th>
                    <th className="text-center p-3 text-xs font-medium text-muted-foreground">Referrals</th>
                    <th className="text-center p-3 text-xs font-medium text-muted-foreground">Revenue</th>
                    <th className="text-center p-3 text-xs font-medium text-muted-foreground">Coffees</th>
                    <th className="text-center p-3 text-xs font-medium text-muted-foreground">Status</th>
                    <th className="text-right p-3 text-xs font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredPartners.map((partner) => {
                    const TypeIcon = PARTNER_TYPE_ICONS[partner.partner_type] || Users;
                    return (
                      <tr 
                        key={partner.id} 
                        className="hover:bg-muted/30 transition-colors"
                        data-testid={`partner-row-${partner.id}`}
                      >
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center">
                              <TypeIcon className="w-4 h-4 text-muted-foreground" />
                            </div>
                            <div>
                              <div className="flex items-center gap-1.5">
                                <p className="font-medium text-foreground">{partner.name}</p>
                                {partner.is_vip && (
                                  <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground">{partner.phone}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <code className="text-sm font-mono bg-muted px-2 py-1 rounded">{partner.code}</code>
                        </td>
                        <td className="p-3 text-center font-medium">{partner.total_referrals || 0}</td>
                        <td className="p-3 text-center font-medium text-green-600">${(partner.total_accumulated || 0).toFixed(0)}</td>
                        <td className="p-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Coffee className="w-4 h-4 text-orange-500" />
                            <span className="font-medium">{partner.coffees_earned || 0}</span>
                            <span className="text-xs text-muted-foreground">
                              ({(partner.coffees_earned || 0) - (partner.coffees_redeemed || 0)} left)
                            </span>
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            partner.status === 'active' 
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : partner.status === 'suspended'
                              ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                              : 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
                          }`}>
                            {partner.status === 'active' ? (
                              <UserCheck className="w-3 h-3" />
                            ) : (
                              <UserX className="w-3 h-3" />
                            )}
                            {partner.status}
                          </span>
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {partner.status === 'active' ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => updatePartnerStatus(partner.id, 'suspended')}
                                data-testid={`suspend-partner-${partner.id}`}
                              >
                                Suspend
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 px-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => updatePartnerStatus(partner.id, 'active')}
                                data-testid={`activate-partner-${partner.id}`}
                              >
                                Activate
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredPartners.length === 0 && (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-muted-foreground">
                        <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                        <p>No partners found</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Revenue by Type Breakdown */}
      {stats?.partners?.by_type && Object.keys(stats.partners.by_type).length > 0 && (
        <div className="bg-card rounded-xl border border-border p-6">
          <h3 className="font-semibold text-foreground mb-4">Revenue by Partner Type</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(stats.partners.by_type).map(([type, data]) => {
              const TypeIcon = PARTNER_TYPE_ICONS[type] || Users;
              return (
                <div key={type} className="p-4 bg-muted/30 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <TypeIcon className="w-5 h-5 text-primary" />
                    <span className="font-medium text-foreground">{PARTNER_TYPE_LABELS[type] || type}</span>
                  </div>
                  <p className="text-2xl font-bold text-foreground">${(data.revenue || 0).toFixed(0)}</p>
                  <p className="text-xs text-muted-foreground">{data.count || 0} partners</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
